// B3-07 (2026-10-01 completion directive) — the previous protection for
// a memory-only failed-measurement draft (B2-03's `persisted: false`
// state, used only when BOTH the server save AND the localStorage
// write failed) lived entirely inside `useMeasurementController`'s own
// `beforeunload` effect. That effect is torn down the instant the
// sheet viewer unmounts — an ordinary in-app route change away from
// the sheet (to the dashboard, another project, anywhere) removes the
// listener while the draft itself is still only sitting in
// `failedMeasurementDrafts.js`'s module-level memory fallback. From
// that point on, closing the tab or reloading loses it silently, with
// no warning at all.
//
// This banner is mounted once in `Shell.jsx`, which stays mounted for
// the entire authenticated session regardless of route, so the
// `beforeunload` guard and the visible warning now outlive any single
// page. It is also the one honest, usable recovery path for the case
// `beforeunload` CANNOT cover at all: a backgrounded mobile tab that
// the OS kills outright never fires `beforeunload` (iOS Safari and
// Android Chrome both reclaim backgrounded tabs without one) — the
// Export button lets the operator pull the exact geometry/value/unit
// out as a file the moment they notice this banner, independent of
// whether any unload event ever fires.
import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, Download } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { loadFailedDrafts, subscribeToFailedDrafts } from "@/lib/failedMeasurementDrafts";
import { toast } from "sonner";

export default function UnsavedDraftsBanner() {
  const { user } = useAuth();
  const identityKey = user ? `${user.tenant_id}:${user.id}` : null;
  const [memoryOnly, setMemoryOnly] = useState([]);

  const refresh = useCallback((all) => {
    setMemoryOnly((all || []).filter((d) => d.persisted === false));
  }, []);

  useEffect(() => {
    if (!identityKey) {
      setMemoryOnly([]);
      return;
    }
    refresh(loadFailedDrafts(identityKey));
    return subscribeToFailedDrafts(identityKey, refresh);
  }, [identityKey, refresh]);

  // Honest about what this can and can't prevent: a standard in-page
  // confirmation on tab close/reload, not a guarantee against the
  // mobile OS reclaiming a backgrounded tab (see file header).
  useEffect(() => {
    if (memoryOnly.length === 0) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [memoryOnly]);

  if (memoryOnly.length === 0) return null;

  const exportDrafts = () => {
    const payload = memoryOnly.map((d) => ({
      sheet_id: d.sheetId,
      saved_at: d.savedAt,
      reason: d.reason,
      measurement: d.payload,
    }));
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `forgedops-unsaved-measurements-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${memoryOnly.length} unsaved measurement${memoryOnly.length === 1 ? "" : "s"}.`);
  };

  return (
    <div
      className="px-4 py-2 text-[12px] font-medium flex flex-wrap items-center justify-between gap-x-3 gap-y-1 min-w-0 bg-[#3a0f0f] text-red-100 border-b border-red-800/60"
      data-testid="unsaved-drafts-banner"
    >
      <div className="inline-flex items-center gap-2 min-w-0">
        <AlertTriangle size={14} className="shrink-0" />
        <span data-testid="unsaved-drafts-banner-message">
          {memoryOnly.length} measurement{memoryOnly.length === 1 ? "" : "s"} saved only in this tab's memory —
          this device's storage rejected the save, so closing or reloading this tab (or your phone reclaiming it
          in the background) will lose {memoryOnly.length === 1 ? "it" : "them"}. Export now to be safe.
        </span>
      </div>
      <button
        onClick={exportDrafts}
        className="inline-flex items-center gap-1.5 rounded-full border border-current/40 px-3 py-1 text-[11px] uppercase tracking-wide hover:bg-white/10 transition-colors shrink-0"
        data-testid="unsaved-drafts-banner-export-btn"
      >
        <Download size={12} />
        Export
      </button>
    </div>
  );
}
